/**
 * 销售目标编辑：直接显示已自动生成的目标，同时保留每个字段的自动 / 手动状态。
 * 未修改的自动值保存时仍传 0，避免仅打开保存就把分公司规则固化为个人覆盖值。
 * 清空或填 0 恢复自动；现有手动目标继续保留。
 */
import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { fmtMoney } from "../lib/appUtils";

const REGION_ZH = { la: "南加州（洛杉矶）", norcal: "北加州", sd: "圣地亚哥" };
const REGION_EN = { la: "SoCal (LA)", norcal: "NorCal", sd: "San Diego" };
const TARGET_FIELDS = ["monthSign", "monthCollect", "yearSign", "yearCollect"];

/**
 * 单个金额字段。field 保存输入文本与自动标记，onChange 只更新草稿，不请求后端。
 * 组件必须定义在弹窗外：原先内部定义 Row，每次输入都会更换 React 组件类型并重建
 * input，导致输入一个数字就丢失焦点；固定组件身份后连续输入、删除与 Tab 均可正常使用。
 */
function TargetField({ name, label, field, autoValue, autoHint, onChange, disabled, zh }) {
  const id = `target-${name}`;
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</label>
      <Input id={id} type="number" min="0" step="0.01" value={field.value}
        placeholder={`${zh ? "自动" : "auto"} ${fmtMoney(autoValue || 0)}`}
        onChange={(e) => onChange(name, e.target.value)} disabled={disabled}
        aria-describedby={`${id}-hint`} className="mt-1" data-testid={id} />
      <p id={`${id}-hint`} className="text-[10px] text-slate-400 mt-0.5" data-testid={`${id}-hint`}>
        {field.automatic ? autoHint : (zh ? "手动设置（清空恢复自动）" : "Manual (clear to restore auto)")}
      </p>
    </div>
  );
}

/** salesName 指定考核销售；弹窗由调用方按打开状态挂载，关闭时丢弃未保存草稿。 */
export default function TargetDialog({ salesName, open, onClose }) {
  const { lang, salesTargets, setSalesTarget } = useApp();
  const zh = lang === "zh";
  const cur = salesTargets[salesName] || {};
  const auto = cur.auto || {};
  const df = cur.defaults || {};
  const autoValues = cur.autoValues || {
    monthSign: (df.dealsPerMonth || 0) * (df.dealPrice || 0),
    yearSign: 12 * (df.dealsPerMonth || 0) * (df.dealPrice || 0),
    monthCollect: cur.monthCollect || 0,
    yearCollect: cur.yearCollect || 0,
  };
  const [f, setF] = useState(() => Object.fromEntries(TARGET_FIELDS.map((k) => [k, {
    value: String(cur[k] ?? ""), automatic: auto[k] ?? !cur[k],
  }])));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, value) => {
    setF((x) => ({ ...x, [k]: { value, automatic: value === "" || Number(value) === 0 } }));
    setError("");
  };
  // 按字段发送覆盖值；自动字段传 0，不能把界面显示的计算结果当成新的手动目标。
  const save = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await setSalesTarget(salesName, Object.fromEntries(TARGET_FIELDS.map((k) => [k, f[k].automatic ? 0 : Number(f[k].value)])));
      onClose();
    } catch (e) {
      // 保存失败保留草稿，让用户直接重试，避免丢失已经输入的个人目标。
      setError(e?.detailText || (zh ? "保存失败，请重试。" : "Could not save. Please try again."));
    }
    finally { setSaving(false); }
  };
  const regionName = cur.region ? (zh ? REGION_ZH[cur.region] : REGION_EN[cur.region]) || cur.region : (zh ? "未分配分公司" : "no branch");
  const fields = [
    { name: "monthSign", label: zh ? "本月签单目标" : "Monthly Sign", autoHint: zh ? `自动：${df.dealsPerMonth ?? 0} 单 × ${fmtMoney(df.dealPrice || 0)}` : `auto: ${df.dealsPerMonth ?? 0} × ${fmtMoney(df.dealPrice || 0)}` },
    { name: "monthCollect", label: zh ? "本月回款目标" : "Monthly Collect", autoHint: zh ? "自动：本月需收款项目待收合计" : "auto: outstanding due this month" },
    { name: "yearSign", label: zh ? "年度签单目标" : "Annual Sign", autoHint: zh ? "自动：分公司月签单默认 × 12" : "auto: branch monthly default × 12" },
    { name: "yearCollect", label: zh ? "年度回款目标" : "Annual Collect", autoHint: zh ? "自动：本年已收 + 需收款项目待收" : "auto: YTD collected + outstanding" },
  ];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-w-lg" data-testid="target-dialog" aria-describedby="target-defaults-hint" closeDisabled={saving} closeLabel={zh ? "关闭" : "Close"}>
        <DialogHeader><DialogTitle>{zh ? `设置销售目标 · ${salesName}` : `Sales Targets · ${salesName}`}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
        <div id="target-defaults-hint" className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 space-y-0.5" data-testid="target-defaults-hint">
          <p className="font-semibold text-slate-800">{zh ? `分公司默认 · ${regionName}` : `Branch default · ${regionName}`}</p>
          <p>{zh ? `每月签 ${df.dealsPerMonth ?? "—"} 单 × 单价 ${fmtMoney(df.dealPrice || 0)} = 月签单目标 ${fmtMoney((df.dealsPerMonth || 0) * (df.dealPrice || 0))}；年度签单 = 12 个月 = ${fmtMoney(12 * (df.dealsPerMonth || 0) * (df.dealPrice || 0))}`
            : `${df.dealsPerMonth ?? "—"} deals/mo × ${fmtMoney(df.dealPrice || 0)} = ${fmtMoney((df.dealsPerMonth || 0) * (df.dealPrice || 0))}/mo; annual = ×12`}</p>
          <p>{zh ? `回款目标按名下需收款项目动态计算：当前待收 ${fmtMoney(autoValues.monthCollect || 0)}（${df.dueProjects ?? 0} 个项目）· 本年已收 ${fmtMoney(df.ytdCollected || 0)}`
            : `Collection targets follow projects needing collection: due ${fmtMoney(autoValues.monthCollect || 0)} (${df.dueProjects ?? 0} projects) · YTD collected ${fmtMoney(df.ytdCollected || 0)}`}</p>
          <p className="text-slate-400">{zh ? "目标已自动生成，已有手动设置优先；修改数字可覆盖，清空或填 0 恢复自动。" : "Targets are generated automatically; existing overrides are kept. Edit to override, or clear / enter 0 to restore auto."}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {fields.map((field) => <TargetField key={field.name} {...field} field={f[field.name]} autoValue={autoValues[field.name]} onChange={set} disabled={saving} zh={zh} />)}
        </div>
        {error && <p role="alert" className="text-sm text-destructive" data-testid="target-save-error">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>{zh ? "取消" : "Cancel"}</Button>
          <Button type="submit" disabled={saving} data-testid="target-save-btn" className="bg-brand-500 hover:bg-brand-600 text-white">{saving ? (zh ? "保存中…" : "Saving…") : (zh ? "保存" : "Save")}</Button>
        </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
